import { $convertFromMarkdownString, $convertToMarkdownString, TRANSFORMERS, type TextMatchTransformer } from '@lexical/markdown'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { LexicalNodeMenuPlugin } from '@lexical/react/LexicalNodeMenuPlugin'
import { LexicalTypeaheadMenuPlugin, MenuOption } from '@lexical/react/LexicalTypeaheadMenuPlugin'
import { Box, Paper, styled, useTheme } from '@mui/material'
import {
    $getSelection,
    $getNodeByKey,
    $isElementNode,
    $isRangeSelection,
    $isTextNode,
    COMMAND_PRIORITY_HIGH,
    COMMAND_PRIORITY_NORMAL,
    IS_BOLD,
    IS_HIGHLIGHT,
    IS_ITALIC,
    IS_STRIKETHROUGH,
    KEY_DOWN_COMMAND,
    PASTE_TAG,
    TextNode,
    type NodeKey,
} from 'lexical'
import { createPortal } from 'react-dom'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { $createReferenceNode, $isReferenceNode, OPEN_REFERENCE_TARGET_PICKER_COMMAND, ReferenceNode } from './ReferenceNode'
import {
    findReferenceChoice,
    normalizeReferenceSearch,
    parseReferenceTokens,
    serializeReferenceToken,
    type ReferenceChoice,
} from './referenceModel'

export const REFERENCE_TRANSFORMER: TextMatchTransformer = {
    dependencies: [ReferenceNode],
    export: (node) => {
        if (!$isReferenceNode(node)) {
            return null
        }
        const format = node.getReferenceFormat()
        const tags = [
            format & IS_BOLD ? '**' : '',
            format & IS_ITALIC ? '*' : '',
            format & IS_STRIKETHROUGH ? '~~' : '',
            format & IS_HIGHLIGHT ? '==' : '',
        ].filter(Boolean)
        return `${tags.join('')}${node.getTextContent()}${tags.reverse().join('')}`
    },
    importRegExp: /::[a-z0-9]+(?:-[a-z0-9]+)*(?::[a-z0-9]+(?:-[a-z0-9]+)*)+(?:@[a-z][a-z0-9]*(?:-[a-z0-9]+)*:[0-9a-f-]{36})?::/,
    regExp: /::[a-z0-9]+(?:-[a-z0-9]+)*(?::[a-z0-9]+(?:-[a-z0-9]+)*)+(?:@[a-z][a-z0-9]*(?:-[a-z0-9]+)*:[0-9a-f-]{36})?::$/,
    replace: (node, match) => {
        const token = parseReferenceTokens(match[0])[0]
        if (token?.raw === match[0]) {
            node.replace($createReferenceNode(token.raw, token.reference, node.getFormat()))
        }
    },
    trigger: ':',
    type: 'text-match',
}

export const REFERENCE_MARKDOWN_TRANSFORMERS = [REFERENCE_TRANSFORMER, ...TRANSFORMERS]

export function $importReferenceMarkdown(markdown: string, preserveNewLines = false) {
    $convertFromMarkdownString(markdown, REFERENCE_MARKDOWN_TRANSFORMERS, undefined, preserveNewLines)
}

export function $exportReferenceMarkdown(preserveNewLines = false) {
    let markdown = $convertToMarkdownString(REFERENCE_MARKDOWN_TRANSFORMERS, undefined, preserveNewLines)
    let previous: string
    do {
        previous = markdown
        // The report renderer treats separate bold spans as separate paragraphs.
        markdown = markdown.replace(/\*\*([^*\n]+)\*\*([ \t]*)\*\*([^*\n]+)\*\*/g, (whole, left, gap, right) =>
            parseReferenceTokens(left).length || parseReferenceTokens(right).length ? `**${left}${gap}${right}**` : whole
        )
    } while (markdown !== previous)
    return markdown
}

export function filterReferenceChoices(choices: ReferenceChoice[], query: string) {
    const normalizedQuery = normalizeReferenceSearch(query)
    return choices.filter((choice) =>
        [choice.label, choice.target_label, ...choice.search_aliases].some(
            (term) => term && normalizeReferenceSearch(term).includes(normalizedQuery)
        )
    )
}

class ReferenceOption extends MenuOption {
    choice: ReferenceChoice

    constructor(choice: ReferenceChoice) {
        super(serializeReferenceToken(choice))
        this.choice = choice
    }
}

class StructureOption extends MenuOption {
    title: string

    constructor(uuid: string, title: string) {
        super(uuid)
        this.title = title
    }
}

function findReferenceTrigger(text: string) {
    const match = /::([^:\n]{0,80})$/.exec(text)
    if (!match || (text.slice(0, match.index).match(/::/g)?.length ?? 0) % 2 === 1) {
        return null
    }
    return {
        leadOffset: match.index,
        matchingString: match[1],
        replaceableString: match[0],
    }
}

export function ReferenceLexicalPlugin({ choices }: { choices: ReferenceChoice[] }) {
    const [editor] = useLexicalComposerContext()
    const theme = useTheme()
    const [query, setQuery] = useState('')
    // The menu must replace the query that opened it, even if the caret moves before selection.
    const lastQuery = useRef<{ key: NodeKey; end: number; text: string } | null>(null)
    const queryMenuOpen = useRef(false)
    const [menuParent, setMenuParent] = useState<HTMLElement>()
    const [pendingReferences, setPendingReferences] = useState<{ key: NodeKey; fieldId: string }[] | null>(null)
    const options = useMemo(() => filterReferenceChoices(choices, query).map((choice) => new ReferenceOption(choice)), [choices, query])
    const structureFieldIds = useMemo(
        () => new Set(choices.filter((choice) => choice.target?.kind === 'structure').map((choice) => choice.field_id)),
        [choices]
    )
    const structureKeysToBind = useMemo(
        () => pendingReferences?.filter(({ fieldId }) => structureFieldIds.has(fieldId)).map(({ key }) => key),
        [pendingReferences, structureFieldIds]
    )
    const structureOptions = useMemo(() => {
        const titles = new Map<string, string>()
        choices.forEach((choice) => {
            if (choice.target?.kind !== 'structure') {
                return
            }
            if (!titles.has(choice.target.uuid) || choice.target_label) {
                titles.set(choice.target.uuid, choice.target_label ?? `Bauliche Anlage ${choice.target.uuid}`)
            }
        })
        const titleCounts = new Map<string, number>()
        titles.forEach((title) => titleCounts.set(title, (titleCounts.get(title) ?? 0) + 1))
        return [...titles].map(
            ([uuid, title]) => new StructureOption(uuid, titleCounts.get(title) === 1 ? title : `${title} · ${uuid.slice(-8)}`)
        )
    }, [choices])

    const raiseMenuAnchor = useCallback(
        (menu: HTMLDivElement | null) => {
            if (menu?.parentElement) {
                menu.parentElement.style.zIndex = String(theme.zIndex.tooltip + 1)
            }
        },
        [theme.zIndex.tooltip]
    )

    const restoreQuerySelection = useCallback(() => {
        if (!lastQuery.current) {
            return
        }
        const { key, end, text } = lastQuery.current
        const node = $getNodeByKey(key)
        if ($isTextNode(node) && node.isSimpleText() && node.getTextContent().slice(end - text.length, end) === text) {
            node.select(end, end)
        }
    }, [])

    useEffect(
        () =>
            editor.registerRootListener((root) => {
                const dialog = root?.closest('[role="dialog"]')
                setMenuParent(dialog instanceof HTMLElement ? dialog : undefined)
            }),
        [editor]
    )

    useEffect(
        () =>
            editor.registerNodeTransform(TextNode, (node) => {
                if ($isReferenceNode(node) || !node.isSimpleText()) {
                    return
                }
                const text = node.getTextContent()
                const token = parseReferenceTokens(text)[0]
                if (!token) {
                    return
                }
                const matchingNode = token.start === 0 ? node.splitText(token.end)[0] : node.splitText(token.start, token.end)[1]
                if (!matchingNode) {
                    return
                }
                matchingNode.replace($createReferenceNode(token.raw, token.reference, matchingNode.getFormat()))
            }),
        [editor]
    )

    useEffect(
        () =>
            editor.registerMutationListener(
                ReferenceNode,
                (mutations, { updateTags }) => {
                    const pastedReferences = updateTags.has(PASTE_TAG)
                        ? editor.getEditorState().read(() =>
                              [...mutations].flatMap(([key, mutation]) => {
                                  const node = mutation === 'created' ? $getNodeByKey(key) : null
                                  return $isReferenceNode(node) && !node.getReference().target
                                      ? [{ key, fieldId: node.getReference().field_id }]
                                      : []
                              })
                          )
                        : []
                    setPendingReferences((current) => {
                        const remaining = current?.filter(({ key }) => mutations.get(key) !== 'destroyed') ?? []
                        return remaining.length || pastedReferences.length ? [...remaining, ...pastedReferences] : null
                    })
                },
                { skipInitialization: true }
            ),
        [editor]
    )

    useEffect(() => {
        if (!pendingReferences?.length) {
            return
        }
        editor.update(() => {
            pendingReferences.forEach(({ key, fieldId }) => {
                const target = choices.find((choice) => choice.field_id === fieldId && choice.target?.kind === 'economic-unit')?.target
                const node = $getNodeByKey(key)
                if (!target || !$isReferenceNode(node) || node.getReference().target) {
                    return
                }
                const reference = { ...node.getReference(), target }
                node.replace($createReferenceNode(serializeReferenceToken(reference), reference, node.getReferenceFormat()))
            })
        })
    }, [choices, editor, pendingReferences])

    useEffect(
        () =>
            editor.registerCommand(
                OPEN_REFERENCE_TARGET_PICKER_COMMAND,
                (key) => {
                    const node = $getNodeByKey(key)
                    if (!$isReferenceNode(node)) {
                        return false
                    }
                    const reference = node.getReference()
                    if (findReferenceChoice(reference, choices) || !structureFieldIds.has(reference.field_id)) {
                        return false
                    }
                    editor.getRootElement()?.focus({ preventScroll: true })
                    setPendingReferences([{ key, fieldId: reference.field_id }])
                    return true
                },
                COMMAND_PRIORITY_NORMAL
            ),
        [choices, editor, structureFieldIds]
    )

    useEffect(() => {
        if (!pendingReferences?.length) {
            return
        }
        const isStructureMenu = (target: EventTarget | null) =>
            target instanceof Element && !!target.closest('[data-reference-structure-menu]')
        const closeOnOutsidePointer = (event: PointerEvent) => {
            if (!isStructureMenu(event.target)) {
                setPendingReferences(null)
            }
        }
        const closeOnFocusAway = (event: FocusEvent) => {
            if (event.target instanceof Node && editor.getRootElement()?.contains(event.target)) {
                return
            }
            if (!isStructureMenu(event.target)) {
                setPendingReferences(null)
            }
        }
        document.addEventListener('pointerdown', closeOnOutsidePointer, true)
        document.addEventListener('focusin', closeOnFocusAway, true)
        return () => {
            document.removeEventListener('pointerdown', closeOnOutsidePointer, true)
            document.removeEventListener('focusin', closeOnFocusAway, true)
        }
    }, [editor, pendingReferences])

    useEffect(
        () =>
            editor.registerCommand(
                KEY_DOWN_COMMAND,
                (event) => {
                    if ((event.key === 'Enter' || event.key === 'Tab') && queryMenuOpen.current) {
                        restoreQuerySelection()
                        return false
                    }
                    if (
                        event.key.length !== 1 ||
                        event.altKey ||
                        event.ctrlKey ||
                        event.metaKey ||
                        event.isComposing ||
                        editor.isComposing()
                    ) {
                        return false
                    }
                    const selection = $getSelection()
                    if (!$isRangeSelection(selection) || !selection.isCollapsed() || selection.anchor.type !== 'element') {
                        return false
                    }
                    const parent = selection.anchor.getNode()
                    if (!$isElementNode(parent)) {
                        return false
                    }
                    const offset = selection.anchor.offset
                    if (!$isReferenceNode(parent.getChildAtIndex(offset - 1)) && !$isReferenceNode(parent.getChildAtIndex(offset))) {
                        return false
                    }
                    event.preventDefault()
                    selection.insertText(event.key)
                    return true
                },
                COMMAND_PRIORITY_HIGH
            ),
        [editor, restoreQuerySelection]
    )

    const onSelectOption = useCallback((option: ReferenceOption, textNode: TextNode | null, closeMenu: () => void) => {
        if (!textNode || !lastQuery.current || !textNode.getTextContent().startsWith(lastQuery.current.text)) {
            lastQuery.current = null
            closeMenu()
            return
        }
        const reference = option.choice
        const text = textNode.getTextContent()
        const queryEnd = text.trimEnd().length
        const matchedNode = queryEnd < text.length ? textNode.splitText(queryEnd)[0] : textNode
        const inserted = $createReferenceNode(
            serializeReferenceToken(reference),
            { field_id: reference.field_id, target: reference.target },
            matchedNode.getFormat()
        )
        matchedNode.replace(inserted)
        inserted.selectNext(0, 0)
        lastQuery.current = null
        closeMenu()
    }, [])

    const onSelectStructure = useCallback(
        (option: StructureOption, _textNode: TextNode | null, closeMenu: () => void) => {
            structureKeysToBind?.forEach((key) => {
                const node = $getNodeByKey(key)
                if (!$isReferenceNode(node)) {
                    return
                }
                const reference = node.getReference()
                if (findReferenceChoice(reference, choices) || !structureFieldIds.has(reference.field_id)) {
                    return
                }
                const boundReference = { ...reference, target: { kind: 'structure', uuid: option.key } }
                node.replace($createReferenceNode(serializeReferenceToken(boundReference), boundReference, node.getReferenceFormat()))
            })
            closeMenu()
            setPendingReferences(null)
        },
        [choices, structureKeysToBind, structureFieldIds]
    )

    return (
        <>
            <LexicalTypeaheadMenuPlugin
                commandPriority={COMMAND_PRIORITY_NORMAL}
                parent={menuParent}
                onOpen={() => {
                    queryMenuOpen.current = true
                }}
                onClose={() => {
                    queryMenuOpen.current = false
                }}
                onQueryChange={(nextQuery) => {
                    setQuery(nextQuery ?? '')
                    const selection = $getSelection()
                    if (nextQuery !== null && $isRangeSelection(selection) && selection.isCollapsed() && selection.anchor.type === 'text') {
                        lastQuery.current = { key: selection.anchor.key, end: selection.anchor.offset, text: `::${nextQuery}` }
                    }
                }}
                onSelectOption={onSelectOption}
                options={options}
                triggerFn={findReferenceTrigger}
                menuRenderFn={(anchor, { options: matches, selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) =>
                    anchor.current &&
                    createPortal(
                        <SuggestionList ref={raiseMenuAnchor} role="listbox" aria-label="Referenzen">
                            {matches.length ? (
                                matches.map((option, index) => (
                                    <Suggestion
                                        key={option.key}
                                        id={`typeahead-item-${index}`}
                                        ref={(element) => option.setRefElement(element)}
                                        role="option"
                                        aria-selected={selectedIndex === index}
                                        $selected={selectedIndex === index}
                                        onPointerDown={(event) => {
                                            if (event.button !== 0) {
                                                return
                                            }
                                            event.preventDefault()
                                            editor.update(restoreQuerySelection)
                                            selectOptionAndCleanUp(option)
                                        }}
                                        onMouseEnter={() => setHighlightedIndex(index)}
                                    >
                                        <Box>{option.choice.label}</Box>
                                        {option.choice.target_label && <SecondaryText>{option.choice.target_label}</SecondaryText>}
                                        <SecondaryText>
                                            {option.choice.available
                                                ? (option.choice.display_value ?? 'Nicht verfügbar')
                                                : 'Nicht verfügbar'}
                                        </SecondaryText>
                                    </Suggestion>
                                ))
                            ) : (
                                <Box padding={tokens.SizingSpacing100}>Keine Referenzen gefunden</Box>
                            )}
                        </SuggestionList>,
                        anchor.current
                    )
                }
            />
            <LexicalNodeMenuPlugin
                commandPriority={COMMAND_PRIORITY_HIGH}
                parent={menuParent}
                nodeKey={structureKeysToBind?.[0] ?? null}
                onClose={() => setPendingReferences(null)}
                onSelectOption={onSelectStructure}
                options={structureOptions}
                menuRenderFn={(anchor, { options: matches, selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) =>
                    anchor.current &&
                    createPortal(
                        <SuggestionList
                            ref={raiseMenuAnchor}
                            role="listbox"
                            aria-label="Bauliche Anlage auswählen"
                            data-reference-structure-menu
                        >
                            {matches.length ? (
                                matches.map((option, index) => (
                                    <Suggestion
                                        key={option.key}
                                        id={`typeahead-item-${index}`}
                                        ref={(element) => option.setRefElement(element)}
                                        role="option"
                                        aria-selected={selectedIndex === index}
                                        $selected={selectedIndex === index}
                                        onPointerDown={(event) => {
                                            if (event.button !== 0) {
                                                return
                                            }
                                            event.preventDefault()
                                            selectOptionAndCleanUp(option)
                                        }}
                                        onMouseEnter={() => setHighlightedIndex(index)}
                                    >
                                        {option.title}
                                    </Suggestion>
                                ))
                            ) : (
                                <Box padding={tokens.SizingSpacing100}>Keine baulichen Anlagen vorhanden</Box>
                            )}
                        </SuggestionList>,
                        anchor.current
                    )
                }
            />
        </>
    )
}

const SuggestionList = styled(Paper)(() => ({
    backgroundColor: tokens.ColorBackgroundLight,
    maxHeight: tokens.SizingHeightWidgetDouble,
    minWidth: tokens.SizingHeightWidgetDouble,
    overflowY: 'auto',
    padding: tokens.SizingSpacing025,
    boxShadow: tokens.Flyout.boxShadow,
}))

const Suggestion = styled('div', { shouldForwardProp: (prop) => prop !== '$selected' })<{ $selected: boolean }>(({ $selected }) => ({
    backgroundColor: $selected ? tokens.ColorSurfaceAccentBlueDefault : undefined,
    borderRadius: tokens.SizingCornerS,
    cursor: 'pointer',
    padding: `${tokens.SizingSpacing075} ${tokens.SizingSpacing100}`,
}))

const SecondaryText = styled(Box)(() => ({
    color: tokens.ColorTextSubtle,
    fontSize: tokens.FontSizeXs,
}))
